import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { Home, CalendarCheck, Bell, BellOff, User, LogOut, ChevronRight, UserPlus, BellRing } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  enablePushSubscription,
  disablePushSubscription,
  getExistingPushSubscription,
  pushSupportStatus,
  fetchPushConfig,
} from "@/lib/webPushClient";

// Phase B2 — `fmt` is called from non-render contexts (mappers). Locked to
// en-GB shape so it never shows en-US-formatted dates. Render-time formatters
// pull from useLocale().formatDateTime instead.
function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy}, ${hh}:${mi}`;
}

export default function HostMobileDashboard() {
  const { user, logout } = useAuth();
  const [summary, setSummary] = useState(null);
  const [showQuick, setShowQuick] = useState(false);
  const [quick, setQuick] = useState({ visitor_name: "", visitor_email: "", expected_at: "", company: "" });
  // Phase C1.3 — web-push enable state. `permission` is the browser's
  // Notification.permission ("default"|"granted"|"denied") OR "unsupported"
  // when the browser lacks SW/PushManager. `subscribed` reflects whether
  // a server-side subscription row exists for this device.
  const [pushPermission, setPushPermission] = useState("default");
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLiveCapable, setPushLiveCapable] = useState(false);
  const [pushWorking, setPushWorking] = useState(false);

  useEffect(() => {
    setPushPermission(pushSupportStatus());
    (async () => {
      try {
        const cfg = await fetchPushConfig();
        setPushLiveCapable(!!cfg?.enabled);
      } catch { /* non-blocking — server may be down */ }
      try {
        const sub = await getExistingPushSubscription();
        setPushSubscribed(!!sub);
      } catch { /* permission may be blocked — ignore */ }
    })();
  }, []);

  const togglePush = async () => {
    setPushWorking(true);
    try {
      if (pushSubscribed) {
        await disablePushSubscription();
        setPushSubscribed(false);
        toast.success("Push notifications disabled on this device.");
      } else {
        await enablePushSubscription();
        setPushSubscribed(true);
        setPushPermission("granted");
        toast.success("Push notifications enabled — you'll get a buzz when a visitor arrives.");
      }
    } catch (e) {
      const code = e?.message;
      if (code === "PUSH_PERMISSION_DENIED") {
        setPushPermission(e.permission || "denied");
        toast.error("Notification permission was denied. Enable it in your browser settings to receive alerts.");
      } else if (code === "PUSH_DISABLED") {
        toast.error("Web push isn't configured for this organisation yet — ask your admin to enable it.");
      } else if (code === "PUSH_UNSUPPORTED") {
        toast.error("This browser doesn't support push notifications.");
      } else {
        toast.error(`Couldn't enable push: ${code || "unknown error"}`);
      }
    } finally { setPushWorking(false); }
  };

  const refresh = async () => {
    try {
      const { data } = await api.get("/host/me/summary");
      setSummary(data);
      if (!quick.company && data.defaults?.company) setQuick((q) => ({ ...q, company: data.defaults.company }));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); }, []);

  const submitQuick = async () => {
    if (!quick.visitor_name || !quick.visitor_email || !quick.expected_at) {
      return toast.error("Name, email, expected time required");
    }
    try {
      const sites = await api.get("/sites");
      const siteId = sites.data?.[0]?.id;
      await api.post("/pre-registrations", {
        site_id: siteId,
        visitor_type: "Visitor",
        visitor_name: quick.visitor_name,
        visitor_email: quick.visitor_email,
        company: quick.company || null,
        expected_at: new Date(quick.expected_at).toISOString(),
        purpose: "Quick pre-reg",
      });
      toast.success("Pre-registered");
      setShowQuick(false);
      setQuick({ visitor_name: "", visitor_email: "", expected_at: "", company: "" });
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const upcoming = summary?.pre_registrations_upcoming || [];
  const live = summary?.visits_in_progress || [];

  return (
    <div className="min-h-screen bg-surface pb-20" data-testid="host-mobile-dashboard">
      <header className="sticky top-0 z-10 bg-navy-900 text-white px-5 py-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-navy-200">Hi {user?.name?.split(" ")[0] || "there"}</div>
          <div className="text-lg font-bold">My visitors</div>
        </div>
        <button onClick={logout} className="p-2 rounded-full hover:bg-navy-700" data-testid="host-logout"><LogOut className="w-5 h-5" /></button>
      </header>

      <main className="px-4 py-4 space-y-5">
        {/* Phase C1.3 — Web push enable card. Hidden on browsers that don't
            support PushManager (e.g. iOS Safari pre-16.4). */}
        {pushPermission !== "unsupported" && (
          <section data-testid="host-push-card">
            <div className={`rounded-xl p-4 flex items-start gap-3 border ${
              pushSubscribed
                ? "bg-success-50 border-success-200"
                : pushPermission === "denied"
                  ? "bg-danger-50 border-danger-200"
                  : "bg-white border-navy-100"
            }`}>
              <div className={`mt-0.5 ${pushSubscribed ? "text-success-700" : "text-accent"}`}>
                {pushSubscribed ? <BellRing className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-navy-900 text-sm">
                  {pushSubscribed ? "Push notifications are on" : "Enable push notifications"}
                  {!pushLiveCapable && !pushSubscribed && (
                    <span className="ml-2 text-[10px] uppercase font-bold text-warning-700 bg-warning-50 px-1.5 py-0.5 rounded">mocked</span>
                  )}
                </div>
                <div className="text-xs text-navy-500 mt-0.5">
                  {pushSubscribed
                    ? "You'll get a buzz on this device when a visitor arrives for you."
                    : pushPermission === "denied"
                      ? "Notification permission is blocked. Re-enable it in your browser site settings to receive alerts."
                      : "Get a buzz on this device when a visitor arrives — even when the app is closed."}
                </div>
              </div>
              {pushPermission !== "denied" && (
                <Button
                  size="sm"
                  variant={pushSubscribed ? "outline" : "default"}
                  className={pushSubscribed ? "" : "bg-accent hover:bg-accent/90 text-white"}
                  onClick={togglePush}
                  disabled={pushWorking}
                  data-testid="host-push-toggle"
                >
                  {pushSubscribed ? <><BellOff className="w-3.5 h-3.5 mr-1" />Disable</> : <>Enable</>}
                </Button>
              )}
            </div>
          </section>
        )}

        {/* Live now */}
        <section data-testid="host-live-now">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-navy-700">On-site now</h2>
            <span className="text-xs text-navy-500">{live.length}</span>
          </div>
          {live.length === 0 ? (
            <div className="rounded-xl bg-white border border-navy-100 p-4 text-sm text-navy-500">Nobody's here yet.</div>
          ) : (
            <ul className="rounded-xl bg-white border border-navy-100 divide-y divide-navy-100">
              {live.map((v) => (
                <li key={v.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="flex-1">
                    <div className="font-medium text-navy-900">{v.visitor_name}</div>
                    <div className="text-xs text-navy-500">{v.company || ""} · since {fmt(v.signed_in_at)}</div>
                  </div>
                  <span className="text-[10px] uppercase font-bold text-success-700 bg-success-50 px-2 py-0.5 rounded-full">live</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Upcoming pre-regs */}
        <section data-testid="host-upcoming">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-sm font-semibold text-navy-700">Pre-registrations · next 7 days</h2>
            <Button size="sm" onClick={() => setShowQuick((v) => !v)} className="bg-accent hover:bg-accent/90 text-white" data-testid="host-quick-prereg-btn">
              <UserPlus className="w-4 h-4 mr-1" /> Quick pre-reg
            </Button>
          </div>
          {showQuick && (
            <div className="rounded-xl bg-white border border-accent p-4 space-y-2 mb-3">
              <input className="w-full px-3 py-2 border border-navy-200 rounded-lg" placeholder="Visitor name" value={quick.visitor_name}
                onChange={(e) => setQuick({ ...quick, visitor_name: e.target.value })} data-testid="quick-visitor-name" />
              <input className="w-full px-3 py-2 border border-navy-200 rounded-lg" placeholder="Email" value={quick.visitor_email}
                onChange={(e) => setQuick({ ...quick, visitor_email: e.target.value })} data-testid="quick-visitor-email" />
              <input className="w-full px-3 py-2 border border-navy-200 rounded-lg" placeholder="Company (optional)" value={quick.company}
                onChange={(e) => setQuick({ ...quick, company: e.target.value })} />
              <input type="datetime-local" className="w-full px-3 py-2 border border-navy-200 rounded-lg" value={quick.expected_at}
                onChange={(e) => setQuick({ ...quick, expected_at: e.target.value })} data-testid="quick-expected-at" />
              <Button onClick={submitQuick} className="w-full bg-accent hover:bg-accent/90 text-white" data-testid="quick-submit">Pre-register</Button>
            </div>
          )}
          {upcoming.length === 0 ? (
            <div className="rounded-xl bg-white border border-navy-100 p-4 text-sm text-navy-500">No upcoming visitors.</div>
          ) : (
            <ul className="rounded-xl bg-white border border-navy-100 divide-y divide-navy-100">
              {upcoming.map((p) => (
                <li key={p.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="flex-1">
                    <div className="font-medium text-navy-900">{p.visitor_name}</div>
                    <div className="text-xs text-navy-500">{p.visitor_email} · {fmt(p.expected_at)}</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-navy-300" />
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      <nav className="fixed bottom-0 inset-x-0 bg-white border-t border-navy-100 grid grid-cols-4 px-1 py-2" data-testid="host-bottom-nav">
        <Link to="/host/dashboard" className="flex flex-col items-center text-accent text-xs">
          <Home className="w-5 h-5" />Home
        </Link>
        <Link to="/pre-registrations" className="flex flex-col items-center text-navy-600 text-xs">
          <CalendarCheck className="w-5 h-5" />Pre-regs
        </Link>
        <Link to="/notifications" className="flex flex-col items-center text-navy-600 text-xs">
          <Bell className="w-5 h-5" />Alerts
        </Link>
        <Link to="/dashboard" className="flex flex-col items-center text-navy-600 text-xs">
          <User className="w-5 h-5" />More
        </Link>
      </nav>
    </div>
  );
}
